import { doseLogUnitLabel } from '../../shared/dose-log.js';

// What the dose calculator shows, and when. Pure, so the rules have tests.

// While the server doubts the reading a dose is scaled off (a frozen
// tester, an old test, a far-off number), the mL figure waits for the
// keeper's "I checked this number". Shown first, it was dosed first: the
// tick only gated the log, so a dose went in against a reading nobody had
// checked. A 0 mL answer or an at-target answer has no figure to hold.
export function doseFigureHeld(preview, acknowledged) {
  if (!preview?.source_doubt || acknowledged) return false;
  if (preview.already_at_or_above) return false;
  return Number(preview.amount_ml) > 0;
}

// The tick box under the doubt, now that ticking it shows the dose.
export function doubtAckLabel(doubt) {
  if (doubt?.reason === 'trident_disagrees') return 'My own test is right. Show the dose.';
  return doubt?.reason === 'unconfirmed_outlier'
    ? 'I checked this number. Show the dose.'
    : 'This reading is current. Show the dose.';
}

// A doubt the server found only at confirm (the preview was minutes old).
// The calculator puts the question back on screen so the keeper can answer
// it and log the dose, instead of a refusal that leaves a dose already in
// the water unlogged and missing from tomorrow's caps.
export function doubtFromConfirmError(err) {
  const code = err?.payload?.error || err?.message;
  if (code !== 'stale_source_unconfirmed') return null;
  return err?.payload?.source_doubt || null;
}

// The product the calculator is dosing with, for the collapsed "Dosing
// with" row: the keeper's pick, else the one the server chose.
export function chosenProductLabel(preview, productChoice) {
  const key = productChoice || preview?.product_key;
  const listed = (preview?.products || []).find((p) => p.key === key);
  return listed?.label || preview?.product?.label || null;
}

// "1 litre" / "2 litres", "1 gallon" / "5 gallons", for a label volume the
// keeper typed. The unit word before a number is chosen is an ellipsis.
export function labelVolumeWord(vol, volUnit) {
  if (volUnit !== 'L' && volUnit !== 'gal') return '…';
  const one = Number(vol) === 1;
  if (volUnit === 'L') return one ? 'litre' : 'litres';
  return one ? 'gallon' : 'gallons';
}

// Feedback #78: a dose the keeper saved (logged, or confirmed in the
// calculator) can be removed; one a controller reported cannot.
export function canRemoveDose(h) {
  return h?.method === 'log' || h?.method == null;
}

// Removing a dose that did go in would make the next calculation dose it
// again, so the confirm names which dose it is and says it is for one that
// never went in; a different amount is removed and logged again.
export function removeDoseConfirm(h, when = '') {
  const logged = h?.method === 'log' && h?.amount != null;
  const amount = logged
    ? `${Number(h.amount)} ${doseLogUnitLabel(h.unit)}`
    : h?.amount_ml != null
      ? `${Number(h.amount_ml)} mL`
      : '';
  const name = h?.product_label || 'this dose';
  const title = amount ? `Remove ${amount} of ${name}?` : `Remove ${name}?`;
  const saved = when ? `Saved ${when}. ` : '';
  if (h?.method === 'log') {
    return {
      title,
      body: `${saved}Only this logged dose goes. If the amount was typed wrong, log the right one after.`,
    };
  }
  return {
    title,
    body:
      `${saved}Only for a dose that never went in the tank: once removed, it no longer counts ` +
      'toward the daily dose limit or the next dose calculation. Dosed a different amount? ' +
      'Remove it, then log what went in with + Log another dose. If it came out of a bottle in ' +
      'Supplies, the amount goes back, unless you have set or refilled that bottle since.',
  };
}

function supplyUnitWord(unit) {
  const u = String(unit || '').trim();
  if (u.toLowerCase() === 'ml') return 'mL';
  if (u.toLowerCase() === 'l') return 'L';
  return u;
}

export function removedDoseNote(bottles) {
  const parts = ['Dose removed.'];
  for (const b of bottles || []) {
    if (b.restored != null) {
      const n = Math.round(Number(b.restored) * 1000) / 1000;
      parts.push(`${n} ${supplyUnitWord(b.unit)} went back in ${b.name}.`);
    } else if (b.kept === 'edited') {
      parts.push(`${b.name} keeps the amount you set in Supplies since.`);
    } else if (b.kept === 'full') {
      parts.push(`${b.name} is already full.`);
    }
  }
  return parts.join(' ');
}
